import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, XCircle, FileSignature } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [state, setState] = useState('checking');
  const [proposalId, setProposalId] = useState(null);
  const [esignEnabled, setEsignEnabled] = useState(false);
  const attempts = useRef(0);

  const poll = useCallback(async () => {
    const sessionId = new URLSearchParams(window.location.search).get('session_id');
    if (!sessionId) { setState('error'); return; }
    try {
      const res = await fetch(`${API}/api/payments/status/${sessionId}`);
      const data = await res.json();
      if (data.payment_status === 'paid') {
        setProposalId(data.proposal_id);
        setState('paid');
        return;
      }
      if (['failed', 'expired'].includes(data.payment_status)) { setState('failed'); return; }
    } catch (e) { /* retry */ }
    attempts.current += 1;
    if (attempts.current >= 10) { setState('timeout'); return; }
    setTimeout(poll, 2000);
  }, []);

  useEffect(() => {
    fetch(`${API}/api/config/features`).then(r => r.json()).then(f => setEsignEnabled(f.esign_enabled)).catch(() => {});
    poll();
  }, [poll]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-md">
        {state === 'checking' && (
          <>
            <Loader2 className="w-12 h-12 text-gold animate-spin mx-auto mb-6" />
            <h1 className="text-white text-2xl font-semibold mb-2">Confirming your payment…</h1>
            <p className="text-white/40 text-sm">This takes just a few seconds. Don't close this page.</p>
          </>
        )}
        {state === 'paid' && (
          <div data-testid="payment-confirmed">
            <CheckCircle className="w-14 h-14 text-emerald-400 mx-auto mb-6" />
            <h1 className="text-white text-3xl font-semibold mb-3">Deposit received!</h1>
            <p className="text-white/50 text-sm mb-8">Your date is officially reserved. A receipt is on its way to your email.</p>
            {esignEnabled && proposalId ? (
              <button
                onClick={() => navigate(`/sign/${proposalId}`)}
                className="inline-flex items-center gap-2 px-8 py-4 bg-gold text-black font-bold uppercase tracking-wide hover:bg-[#E5B954] transition-colors"
                data-testid="go-sign-contract"
              >
                <FileSignature className="w-5 h-5" /> Sign Your Agreement
              </button>
            ) : (
              <button onClick={() => navigate('/')} className="px-8 py-4 bg-white/10 text-white hover:bg-white/20 transition-colors" data-testid="back-home">
                Back to Home
              </button>
            )}
          </div>
        )}
        {(state === 'failed' || state === 'error' || state === 'timeout') && (
          <div data-testid="payment-problem">
            <XCircle className="w-14 h-14 text-red-400 mx-auto mb-6" />
            <h1 className="text-white text-2xl font-semibold mb-3">
              {state === 'timeout' ? "Still processing…" : "Payment didn't go through"}
            </h1>
            <p className="text-white/50 text-sm mb-8">
              {state === 'timeout'
                ? 'Your payment may still complete — check your email for a receipt, or contact us.'
                : 'No charge was made. You can try again from your estimate.'}
            </p>
            <button onClick={() => navigate('/')} className="px-8 py-4 bg-white/10 text-white hover:bg-white/20 transition-colors">Back to Home</button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
